<script setup>
// A club-branded public page (BOOKINGS_PLAN.md B17, SE18, SE19): the club's
// logo, name and colour as an accent stripe on a light page, Tap Leagues only
// as the footer credit, and a validated return link back to the club's own
// site. Branding is shown for VERIFIED clubs only; everyone else gets Tap
// Leagues branding (SE18). Used by the parent-or-carer consent page, the
// pay-for-someone page and the public join pages; bookings reuse it later.
//
//   <club-branded-frame :club="{name, imageFileName, brandColour, isVerified, websiteUrl}"
//                       :return-url="route.query.returnUrl" credit="Payments by Tap Leagues">
//     ...page body...
//   </club-branded-frame>
import {computed} from 'vue';
import AvatarImageLoader from '~/components/loaders/AvatarImageLoader.vue';
import {legalHref} from '~/composables/useLegalLinks';
import {brandAccent, clubInitials, safeReturnUrl} from '~/utils/membershipsMember';

const props = defineProps({
  /** {name, imageFileName, brandColour, isVerified, websiteUrl, allowedHosts} — only name is needed. */
  club: {type: Object, default: null},
  /** The raw returnUrl (query or API); shown only when it passes safeReturnUrl. */
  returnUrl: {type: String, default: null},
  /** The footer credit's first words. */
  credit: {type: String, default: 'Memberships and payments by Tap Leagues'},
  /** Narrow (forms) or wide (lists). */
  width: {type: String, default: 'narrow'},
});

const verified = computed(() => !!props.club?.isVerified);
const accent = computed(() => verified.value ? brandAccent(props.club?.brandColour, null) : null);
const back = computed(() => safeReturnUrl(props.returnUrl, {
  websiteUrl: props.club?.websiteUrl ?? null,
  allowedHosts: [...(props.club?.allowedHosts ?? []), ...(import.meta.dev ? ['localhost', '127.0.0.1'] : [])],
}));
const initials = computed(() => clubInitials(props.club?.name));
const stripeStyle = computed(() => accent.value ? {background: accent.value} : null);
</script>

<template>
  <div class="cbf" :class="{'cbf--wide': width === 'wide'}">
    <div class="cbf-stripe" :style="stripeStyle"/>
    <div class="cbf-inner">
      <header class="cbf-head">
        <template v-if="verified && club">
          <avatar-image-loader
              v-if="club.imageFileName"
              :image-file-name="club.imageFileName"
              :image-clickable="false"
              rounded="lg"
              size="44"
              class="cbf-logo bg-white"/>
          <div v-else class="cbf-logo cbf-logo--initials" :style="stripeStyle">{{ initials }}</div>
          <div class="cbf-name">{{ club.name }}</div>
        </template>
        <template v-else>
          <img src="/images/icon_red.png" alt="Tap Leagues" class="cbf-logo cbf-logo--tl"/>
          <div class="cbf-name">Tap Leagues<span v-if="club?.name" class="cbf-for"> · {{ club.name }}</span></div>
        </template>
        <a v-if="back" :href="back.href" class="cbf-back" rel="noopener">
          <v-icon size="16" icon="mdi-arrow-left"/>Back to {{ back.host }}
        </a>
      </header>

      <main class="cbf-body">
        <slot/>
      </main>

      <footer class="cbf-foot">
        <span>{{ credit }}</span>
        <span class="cbf-dot">·</span>
        <nuxt-link :to="legalHref('privacy')" target="_blank">Privacy</nuxt-link>
        <span class="cbf-dot">·</span>
        <nuxt-link :to="legalHref('terms')" target="_blank">Terms</nuxt-link>
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
.cbf {
  min-height: 100dvh;
  background: rgb(var(--v-theme-light));
}
.cbf-stripe {
  height: 6px;
  background: rgb(var(--v-theme-primary));
}
.cbf-inner {
  max-width: 520px;
  margin: 0 auto;
  padding: 18px 16px 40px;
}
.cbf--wide .cbf-inner { max-width: 760px; }
.cbf-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 6px 0 18px;
}
.cbf-logo {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  object-fit: cover;
}
.cbf-logo--initials {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.5px;
  background: rgb(var(--v-theme-primary));
}
.cbf-logo--tl {
  width: 40px;
  height: 40px;
}
.cbf-name {
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.3px;
}
.cbf-for {
  font-weight: 500;
  color: rgb(var(--v-theme-textSubtle));
}
.cbf-back {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
.cbf-body { min-height: 200px; }
.cbf-foot {
  margin-top: 28px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));

  a {
    color: rgb(var(--v-theme-textSubtle));
    text-decoration: underline;
  }
}
.cbf-dot { opacity: .6; }
</style>
